import { maxBy, minBy, uniqWith } from 'es-toolkit';
import { createSelector } from 'reselect';
import { BufferAttribute, BufferGeometry, EdgesGeometry, Vector3 } from 'three';

import { BevelCut, BottomBevelCut, Component, isTruss, Member } from '@paragon/contract-library';
import {
  convertDegreesToRadians,
  ConvexPolyhedron3D,
  Direction3D,
  HalfSpace3D,
  Line2D,
  LineSegment2D,
  Plane3D,
  Point2D,
  Point3D,
  Polygon2D,
} from '@paragon/geometry-library';
import {
  calculateLineLocationOfMemberOrNonContinuousBearingGeometry,
  calculateMemberGeometry,
  calculateRelativeMemberGeometry,
  findAndCalculateBaseCuts,
  findAndCalculateEndCuts,
  findAndCalculateLine,
} from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { Point2D as Point2DInterface, Segment3D } from './types';
import { unwrapMember } from './uvMemberUnWrapperUtility';

export const calculateMemberMidline = createSelector(
  (member: Member) => member,
  (member: Member, component: Component) =>
    member.relativeGeometry ? calculateRelativeMemberGeometry(member.relativeGeometry, component) : null,
  (member, memberRelativeGeometry): Segment3D | null => {
    if (member.relativeGeometry) {
      if (!memberRelativeGeometry) return null;
      const { basePoint, endPoint } = memberRelativeGeometry.midline;
      const z = member.thickness / 2;
      return { start: { ...basePoint, z }, end: { ...endPoint, z } };
    }

    // This is a fallback for when we don't have relative geometry. These properties should not be used elsewhere in the
    // client because they don't get updated when the member geometry gets updated.
    const memberWithMidline: Member & {
      midlineStartX: number;
      midlineStartY: number;
      midlineStartZ: number;
      midlineEndX: number;
      midlineEndY: number;
      midlineEndZ: number;
    } = member;
    if (!(
      memberWithMidline.midlineStartX ||
      memberWithMidline.midlineStartY ||
      memberWithMidline.midlineStartZ ||
      memberWithMidline.midlineEndX ||
      memberWithMidline.midlineEndY ||
      memberWithMidline.midlineEndZ
    )) {
      throw new Error('Member does not have midline.');
    }
    return {
      start: {
        x: memberWithMidline.midlineStartX,
        y: memberWithMidline.midlineStartY,
        z: memberWithMidline.midlineStartZ,
      },
      end: {
        x: memberWithMidline.midlineEndX,
        y: memberWithMidline.midlineEndY,
        z: memberWithMidline.midlineEndZ,
      },
    };
  },
);

export const calculateMemberOverallLengthSegment = createSelector(
  calculateMemberGeometry,
  calculateMemberMidline,
  (memberGeometry, midline) => {
    if (!memberGeometry || !midline) return null;
    const geometryPoints = memberGeometry.map((point) => Point2D.fromObject(point));
    const geometryMidlineAsLine = Line2D.fromPoints(Point2D.fromObject(midline.start), Point2D.fromObject(midline.end));

    let basePoint = Point2D.fromObject(midline.start);
    let endPoint = Point2D.fromObject(midline.end);
    for (const point of geometryPoints) {
      const projectedPoint = point.projectOntoLine(geometryMidlineAsLine);
      if (basePoint.distanceTo(endPoint) < basePoint.distanceTo(projectedPoint)) {
        endPoint = projectedPoint;
      } else if (endPoint.distanceTo(basePoint) < endPoint.distanceTo(projectedPoint)) {
        basePoint = projectedPoint;
      }
    }

    const start = { ...basePoint, z: midline.start.z };
    const end = { ...endPoint, z: midline.end.z };
    return { start, end };
  },
);

export function calculateMemberOverallLength(member: Member, component: Component) {
  const memberOverallLengthSegment = calculateMemberOverallLengthSegment(member, component);
  if (!memberOverallLengthSegment) return null;
  return LineSegment2D.fromPointObjects(memberOverallLengthSegment.start, memberOverallLengthSegment.end).length;
}

export const calculateMemberPolygon = createSelector(calculateMemberGeometry, (memberGeometry) => {
  if (!memberGeometry) return null;
  return new Polygon2D(memberGeometry.map((point) => Point2D.fromObject(point)));
});

function calculateMemberZOffset(member: Member, component: Component) {
  if (!member.relativeGeometry?.isBrace) return 0;

  const { relativeGeometry } = member;
  const bracedMember = component.members.find((member) => member.guid === relativeGeometry.bracedMemberGuid);

  if (!bracedMember) return 0;

  const pliesMultiplier = isTruss(component) ? component.numberOfPlies : 1;
  const bracedMemberThickness = bracedMember.thickness * pliesMultiplier;

  switch (relativeGeometry.side) {
    case 'Center':
      switch (relativeGeometry.braceInstallationLocation) {
        case 'Back':
          return -member.thickness;
        case 'Center':
          throw new Error('Brace intersects braced member.');
        case 'Front':
          return bracedMemberThickness;
        default:
          assertExhaustiveSwitch(relativeGeometry.braceInstallationLocation);
      }
    case 'Port':
    case 'Starboard':
      switch (relativeGeometry.braceInstallationLocation) {
        case 'Back':
          return bracedMemberThickness - member.thickness;
        case 'Center':
          return (bracedMemberThickness - member.thickness) / 2;
        case 'Front':
          return 0;
        default:
          assertExhaustiveSwitch(relativeGeometry.braceInstallationLocation);
      }
    default:
      assertExhaustiveSwitch(relativeGeometry.side);
  }
}

function calculateMemberDefinitionLine(member: Member, component: Component) {
  return member.relativeGeometry ? findAndCalculateLine(member.relativeGeometry.lineGuid, component) : null;
}

export const calculateMemberDirectionedMidline = createSelector(
  (member: Member) => member,
  calculateMemberMidline,
  calculateMemberDefinitionLine,
  (member, midline, line) => {
    if (!member.relativeGeometry || !midline || !line) return null;

    const midline2D = LineSegment2D.fromPointObjects(midline.start, midline.end);
    if (line.direction.equals(midline2D.direction)) {
      return midline2D;
    }

    const reversedMidline2D = midline2D.reverse();
    if (line.direction.equals(reversedMidline2D.direction)) {
      return reversedMidline2D;
    }

    throw new Error("A line's midline is not parallel to its direction.");
  },
);

function calculateMemberBevelCuts(member: Member, memberGeometry: readonly Point2DInterface[]) {
  return calculateBevelCut(member.leftBevelCut, memberGeometry, member.thickness, 'Left')
    .concat(calculateBevelCut(member.rightBevelCut, memberGeometry, member.thickness, 'Right'))
    .concat(calculateBottomBevelCut(member.bottomBevelCut, memberGeometry, member.thickness));
}

function calculateBevelCut(
  bevelCut: BevelCut | null,
  memberGeometry: readonly Point2DInterface[],
  memberThickness: number,
  memberSide: 'Left' | 'Right',
) {
  if (!bevelCut) return [];

  const xLocation =
    memberSide === 'Left'
      ? Math.min(...memberGeometry.map((point) => point.x))
      : Math.max(...memberGeometry.map((point) => point.x));

  return asOrientedPlanes(bevelCut, xLocation, memberSide, memberThickness);
}

function asOrientedPlanes(
  bevelCut: BevelCut,
  outsideXLocation: number,
  memberSide: 'Left' | 'Right',
  memberThickness: number,
) {
  switch (bevelCut.cutType) {
    case 'Double': {
      const outsideZLocation = memberThickness / 2;
      return [
        asOrientedPlane('Front', bevelCut.frontAngle, memberSide, outsideXLocation, outsideZLocation),
        asOrientedPlane('Back', bevelCut.backAngle, memberSide, outsideXLocation, outsideZLocation),
      ];
    }
    case 'Single': {
      const outsideZLocation = bevelCut.cutSide === 'Front' ? 0 : memberThickness;
      return [asOrientedPlane(bevelCut.cutSide, bevelCut.angle, memberSide, outsideXLocation, outsideZLocation)];
    }
    default:
      assertExhaustiveSwitch(bevelCut);
  }
}

function asOrientedPlane(
  bevelCutSide: 'Front' | 'Back',
  angle: number,
  memberSide: 'Left' | 'Right',
  outsideXLocation: number,
  outsideZLocation: number,
) {
  const xSign = memberSide === `Left` ? 1 : -1;
  const zSign = bevelCutSide === 'Front' ? -1 : 1;
  const x = xSign * Math.cos(convertDegreesToRadians(angle));
  const z = zSign * Math.sin(convertDegreesToRadians(angle));
  const orientedPlaneNormalDirection = Direction3D.fromDirectionalVector(new Point3D(x, 0, z));
  if (!orientedPlaneNormalDirection) throw new Error('Direction should not be zero.');

  const planeBasePoint = new Point3D(outsideXLocation, 0, outsideZLocation);

  return Plane3D.fromPerpendicularDirectionAndPoint(orientedPlaneNormalDirection, planeBasePoint);
}

function calculateBottomBevelCut(
  bevelCut: BottomBevelCut | null,
  memberGeometry: readonly Point2DInterface[],
  memberThickness: number,
) {
  if (!bevelCut) return [];

  const yLocation = Math.min(...memberGeometry.map(({ y }) => y));

  const outsideZLocation = bevelCut.cutSide === 'Front' ? 0 : memberThickness;

  const zSign = bevelCut.cutSide === 'Front' ? -1 : 1;
  const y = Math.cos(convertDegreesToRadians(bevelCut.angle));
  const z = zSign * Math.sin(convertDegreesToRadians(bevelCut.angle));
  const orientedPlaneNormalDirection = Direction3D.fromDirectionalVector(new Point3D(0, y, z));
  if (!orientedPlaneNormalDirection) throw new Error('Direction should not be zero.');

  const planeBasePoint = new Point3D(0, yLocation, outsideZLocation);

  return [Plane3D.fromPerpendicularDirectionAndPoint(orientedPlaneNormalDirection, planeBasePoint)];
}

function calculatePolyhedron(
  memberGeometry: readonly Point2DInterface[],
  zOffset: number,
  memberThickness: number,
  bevelCuts: readonly Plane3D[],
) {
  const halfSpaces = [
    HalfSpace3D.fromDirectionAndOffsetFromOrigin(Direction3D.out, zOffset),
    HalfSpace3D.fromDirectionAndOffsetFromOrigin(Direction3D.in, -zOffset - memberThickness),
  ];

  for (const bevelCut of bevelCuts) {
    halfSpaces.push(bevelCut.halfSpace);
  }

  const points = memberGeometry.map((point) => new Point3D(point.x, point.y, 0));
  const interiorPoint = points.reduce((sum, point) => sum.add(point), Point3D.origin).scaleBy(1 / points.length);
  for (let index = 0; index < points.length; index += 1) {
    const vertex0 = points[index];
    const vertex1 = points[(index + 1) % points.length];
    halfSpaces.push(
      HalfSpace3D.fromPlaneAndInteriorPoint(
        Plane3D.fromPointsAndDirectionInPlane(vertex0, vertex1, Direction3D.out)!,
        interiorPoint,
      )!,
    );
  }
  const polyhedron = ConvexPolyhedron3D.intersectionOfHalfSpaces(halfSpaces);
  if (!polyhedron) {
    throw new Error('The polyhedron is not valid.');
  }
  return polyhedron;
}

function calculateThreeJsGeometry(
  polyhedron: ConvexPolyhedron3D,
  midline: Segment3D,
  overallLengthSegmentObject: Segment3D,
) {
  const { vertices, triangles: faces, faces: surfaces } = polyhedron.renderData;

  const geometry = new BufferGeometry();
  geometry.setAttribute(
    'position',
    new BufferAttribute(new Float32Array(vertices.flatMap((point) => [point.x, point.y, point.z])), 3),
  );

  const grainDirection = new Vector3(midline.start.x, midline.start.y, midline.start.z)
    .sub(new Vector3(midline.end.x, midline.end.y, midline.end.z))
    .normalize();
  const faceVertexUvs = unwrapMember(vertices, faces, surfaces, grainDirection);
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array(faceVertexUvs.flatMap((uv) => [uv.x, uv.y])), 2));

  const overallLengthSegment = LineSegment2D.fromSegmentObject(overallLengthSegmentObject);
  const overallLength = overallLengthSegment.length;
  const overallLengthLine = overallLengthSegment.toLine();
  geometry.setAttribute(
    'uv1',
    new BufferAttribute(
      new Float32Array(
        vertices.flatMap((vertex) => [
          Point2D.fromObject(vertex).projectedSignedDistanceAlong(overallLengthLine) / overallLength,
          0,
        ]),
      ),
      2,
    ),
  );

  geometry.setIndex(faces.flatMap((face) => face));
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();

  return geometry;
}

export const calculateMemberThreeJsGeometry = createSelector(
  (member: Member) => member,
  calculateMemberGeometry,
  (member: Member, component: Component) => calculateMemberZOffset(member, component),
  calculateMemberMidline,
  calculateMemberOverallLengthSegment,
  (member, memberGeometry, zOffset, midline, overallLengthSegment) => {
    if (!memberGeometry || !midline || !overallLengthSegment) return null;
    const bevelCuts = calculateMemberBevelCuts(member, memberGeometry);
    const polyhedron = calculatePolyhedron(memberGeometry, zOffset, member.thickness, bevelCuts);
    return calculateThreeJsGeometry(polyhedron, midline, overallLengthSegment);
  },
);

export const calculateMemberThreeJsEdgesGeometry = createSelector(calculateMemberThreeJsGeometry, (threeJsGeometry) => {
  if (!threeJsGeometry) return null;
  return new EdgesGeometry(threeJsGeometry, 1);
});

interface FallbackGeometry {
  readonly bufferGeometry: BufferGeometry;
  readonly basePoint?: Point2D;
  readonly endPoint?: Point2D;
}

/**
 * Instead of showing nothing, show the member stretched widely.
 */
const calculateMemberFallbackGeometry = createSelector(
  (member: Member) => member,
  calculateMemberDefinitionLine,
  (member: Member, component: Component) =>
    member.relativeGeometry ? findAndCalculateBaseCuts(member.relativeGeometry, component) : null,
  (member: Member, component: Component) =>
    member.relativeGeometry ? findAndCalculateEndCuts(member.relativeGeometry, component) : null,
  (member: Member, component: Component, lastMemberLineSelectionPoint: Point2DInterface | undefined) =>
    lastMemberLineSelectionPoint,
  (member, definitionLine, baseCuts, endCuts, lastMemberLineSelectionPoint): FallbackGeometry | null => {
    const { relativeGeometry } = member;
    if (!relativeGeometry || !definitionLine) return null;
    const midline = calculateLineLocationOfMemberOrNonContinuousBearingGeometry(
      relativeGeometry,
      definitionLine,
      'Center',
    );
    if (!midline) return null;
    const lastMemberLineSelectionPoint2D =
      lastMemberLineSelectionPoint && Point2D.fromObject(lastMemberLineSelectionPoint);
    const perpendicularVector = definitionLine.direction.rotateQuarterTurn().toVector(relativeGeometry.width * 0.5);
    const basePointOfMemberLine = lastMemberLineSelectionPoint2D?.projectOntoLine(midline) ?? midline.basePoint;
    const sideOne = new Line2D(basePointOfMemberLine.add(perpendicularVector), definitionLine.direction);
    const sideTwo = new Line2D(basePointOfMemberLine.subtract(perpendicularVector), definitionLine.direction);

    const getPointsForCuts = (cutLines: readonly Line2D[]) => {
      const allCutLineIntersections: (Point2D | null)[] = [];
      const sideOneIntersections: (Point2D | null)[] = [];
      const sideTwoIntersections: (Point2D | null)[] = [];
      for (const cutLine of cutLines) {
        // Intersect the cut lines with the member sides
        sideOneIntersections.push(cutLine.intersectWithLine(sideOne));
        sideTwoIntersections.push(cutLine.intersectWithLine(sideTwo));

        // Intersect the cut lines with each other
        const otherCutLines = cutLines.filter((line) => line !== cutLine);
        for (const otherCutLine of otherCutLines) {
          allCutLineIntersections.push(cutLine.intersectWithLine(otherCutLine));
        }
      }

      const sideOnePoint = minBy(sideOneIntersections.filter(notNullish), (point) =>
        point.distanceTo(sideOne.basePoint),
      );
      const sideTwoPoint = minBy(sideTwoIntersections.filter(notNullish), (point) =>
        point.distanceTo(sideTwo.basePoint),
      );
      const cutLineIntersections = uniqWith(
        allCutLineIntersections.filter(notNullish),
        (pointOne, pointTwo) => pointOne.x === pointTwo.x && pointOne.y === pointTwo.y,
      );
      return { sideOnePoint, sideTwoPoint, cutLineIntersections };
    };

    if (!baseCuts || !endCuts) return null;
    const pointsForBaseCuts = getPointsForCuts(baseCuts);
    const { cutLineIntersections: baseCutLineIntersections } = pointsForBaseCuts;
    let { sideOnePoint: basePointSideOne } = pointsForBaseCuts;
    let { sideTwoPoint: basePointSideTwo } = pointsForBaseCuts;
    const pointsForEndCuts = getPointsForCuts(endCuts);
    const { cutLineIntersections: endCutLineIntersections } = pointsForEndCuts;
    let { sideOnePoint: endPointSideOne } = pointsForEndCuts;
    let { sideTwoPoint: endPointSideTwo } = pointsForEndCuts;

    // The user may have chosen to define either base or end cuts first. Depending which they have chosen and which side
    // of the `lastMemberLineSelectionPoint2D` they have chosen for it to be on, we need to extend the unbounded member
    // in the direction that includes the `lastMemberLineSelectionPoint2D`. We do this by taking the unbounded points in
    // both directions and choosing the one that is further away from the existing cut. This works because the base
    // point of the line is the `lastMemberLineSelectionPoint2D`.

    const distanceToRepresentNoCuts = 100 * 12;
    if (!basePointSideOne) {
      if (endPointSideOne && endPointSideTwo && lastMemberLineSelectionPoint) {
        // End cuts supplied but not base cuts
        basePointSideOne = maxBy(
          [sideOne.getPointAlongLine(distanceToRepresentNoCuts), sideOne.getPointAlongLine(-distanceToRepresentNoCuts)],
          (point) => point.distanceTo(endPointSideOne!),
        );
        basePointSideTwo = maxBy(
          [sideTwo.getPointAlongLine(distanceToRepresentNoCuts), sideTwo.getPointAlongLine(-distanceToRepresentNoCuts)],
          (point) => point.distanceTo(endPointSideTwo!),
        );
      } else {
        // Neither side cuts supplied or base points of sides is not the last member line selection point
        basePointSideOne = sideOne.getPointAlongLine(distanceToRepresentNoCuts);
        basePointSideTwo = sideTwo.getPointAlongLine(distanceToRepresentNoCuts);
      }
    }
    if (!endPointSideOne) {
      if (basePointSideOne && basePointSideTwo && lastMemberLineSelectionPoint) {
        // Base cuts supplied but not end cuts
        endPointSideOne = maxBy(
          [sideOne.getPointAlongLine(distanceToRepresentNoCuts), sideOne.getPointAlongLine(-distanceToRepresentNoCuts)],
          (point) => point.distanceTo(basePointSideOne!),
        );
        endPointSideTwo = maxBy(
          [sideTwo.getPointAlongLine(distanceToRepresentNoCuts), sideTwo.getPointAlongLine(-distanceToRepresentNoCuts)],
          (point) => point.distanceTo(basePointSideTwo!),
        );
      } else {
        // Neither side cuts supplied or base points of sides is not the last member line selection point
        endPointSideOne = sideOne.getPointAlongLine(-distanceToRepresentNoCuts);
        endPointSideTwo = sideTwo.getPointAlongLine(-distanceToRepresentNoCuts);
      }
    }

    if (!basePointSideOne || !basePointSideTwo || !endPointSideOne || !endPointSideTwo) {
      console.warn('Building fall back geometry for member failed.');
      return null;
    }

    // The order of this array is important for calling `getPolyhedron` below.
    const points = [
      basePointSideOne,
      endPointSideOne,
      ...endCutLineIntersections,
      endPointSideTwo,
      basePointSideTwo,
      ...baseCutLineIntersections,
    ];

    const temporaryMidlineStart = definitionLine.getPointAlongLine(-100 * 12);
    const temporaryMidlineEnd = definitionLine.getPointAlongLine(100 * 12);
    const temporaryMidline = {
      start: {
        x: temporaryMidlineStart.x,
        y: temporaryMidlineStart.y,
        z: member.thickness / 2,
      },
      end: {
        x: temporaryMidlineEnd.x,
        y: temporaryMidlineEnd.y,
        z: member.thickness / 2,
      },
    };

    // It's possible that the user has defined invalid cuts, in which case we cannot generate a valid polyhedron. In
    // this case, fall back to an elongated member without cuts.
    try {
      const polyhedron = calculatePolyhedron(points, 0, member.thickness, []);
      const bufferGeometry = calculateThreeJsGeometry(polyhedron, temporaryMidline, temporaryMidline);
      return {
        bufferGeometry,
        basePoint: basePointSideOne,
        endPoint: endPointSideOne,
      };
    } catch {
      const simpleElongatedMemberPoints = [
        sideOne.getPointAlongLine(-distanceToRepresentNoCuts),
        sideOne.getPointAlongLine(distanceToRepresentNoCuts),
        sideTwo.getPointAlongLine(distanceToRepresentNoCuts),
        sideTwo.getPointAlongLine(-distanceToRepresentNoCuts),
      ];
      const polyhedron = calculatePolyhedron(simpleElongatedMemberPoints, 0, member.thickness, []);
      const bufferGeometry = calculateThreeJsGeometry(polyhedron, temporaryMidline, temporaryMidline);
      return {
        bufferGeometry,
      };
    }
  },
);

export const calculateMemberMidlineAlignedWithCutsOrFallback = createSelector(
  calculateMemberMidline,
  calculateMemberFallbackGeometry,
  (memberMidline, memberFallbackGeometry) =>
    memberMidline
      ? {
          start: { x: memberMidline.start.x, y: memberMidline.start.y },
          end: { x: memberMidline.end.x, y: memberMidline.end.y },
        }
      : memberFallbackGeometry?.basePoint && memberFallbackGeometry?.endPoint
        ? {
            start: { x: memberFallbackGeometry.basePoint.x, y: memberFallbackGeometry.basePoint.y },
            end: { x: memberFallbackGeometry.endPoint.x, y: memberFallbackGeometry.endPoint.y },
          }
        : null,
);

export const calculateMemberThreeJsGeometryOrFallback = createSelector(
  calculateMemberThreeJsGeometry,
  calculateMemberFallbackGeometry,
  (memberThreeJsGeometry, memberFallbackGeometry) => memberThreeJsGeometry ?? memberFallbackGeometry?.bufferGeometry,
);
